import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEyeSlash, FaFont, FaMinus, FaPlus } from 'react-icons/fa';
import { useAccessibility } from './AccessibilityProvider';

const ToolbarContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: var(--primary-600);
  color: white;
  padding: var(--space-sm);
  border-radius: var(--radius-md) 0 0 var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-fixed);
  transition: all var(--transition-fast);

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    right: 0;
    top: auto;
    transform: none;
    border-radius: var(--radius-md) 0 0 0;
    width: 100%;
    max-width: 300px;
  }
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-bottom: var(--space-xs);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin-right: var(--space-xs);
    margin-bottom: 0;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const AccessibilityToolbar = () => {
  const { t } = useTranslation();
  const {
    highContrast,
    reducedMotion,
    fontSize,
    toggleHighContrast,
    toggleReducedMotion,
    increaseFontSize,
    decreaseFontSize,
  } = useAccessibility();

  return (
    <ToolbarContainer>
      <ToolbarRow>
        <ToolbarButton
          onClick={toggleHighContrast}
          aria-label={highContrast ? t('accessibility.disableHighContrast') : t('accessibility.enableHighContrast')}
          title={highContrast ? t('accessibility.disableHighContrast') : t('accessibility.enableHighContrast')}
        >
          {highContrast ? <FaEyeSlash /> : <FaEye />}
        </ToolbarButton>

        <ToolbarButton
          onClick={toggleReducedMotion}
          aria-label={reducedMotion ? t('accessibility.disableReducedMotion') : t('accessibility.enableReducedMotion')}
          title={reducedMotion ? t('accessibility.disableReducedMotion') : t('accessibility.enableReducedMotion')}
        >
          <FaFont />
        </ToolbarButton>

        <ToolbarButton
          onClick={decreaseFontSize}
          aria-label={t('accessibility.decreaseFontSize')}
          title={t('accessibility.decreaseFontSize')}
          disabled={fontSize === 'normal'}
        >
          <FaMinus />
        </ToolbarButton>

        <ToolbarButton
          onClick={increaseFontSize}
          aria-label={t('accessibility.increaseFontSize')}
          title={t('accessibility.increaseFontSize')}
          disabled={fontSize === 'x-large'}
        >
          <FaPlus />
        </ToolbarButton>
      </ToolbarRow>
    </ToolbarContainer>
  );
};

export default AccessibilityToolbar; 